import { Redirect, Route, RouteProps } from 'react-router-dom'
import React, { useContext } from 'react'
import { useLoginContext } from 'stagedive/LoginContext'
import { Right } from '../../../generated/graphql'
import { ExperimentTreatment } from '@amzn/aapn-react-components/dist/Experiments/experiments_io'
import { ExperimentsContext } from '@amzn/aapn-react-components'
import { getMetricsPublisherChainAsync } from '@amzn/aapn-front-end-metrics'
import { useLanguageContext } from 'stagedive/LanguageContext'

export interface Experiment {
  experimentKey: string
  experimentEvaluator: (treatment: ExperimentTreatment) => boolean
}

interface AuthorizedRouteProps extends RouteProps {
  requiredRights: Right[]
  requiredExperiment?: Experiment
  requiredLanguage?: string
}

export const AuthorizedRoute: React.FunctionComponent<AuthorizedRouteProps> = (props: AuthorizedRouteProps): JSX.Element => {
  const { loginContext } = useLoginContext()
  const { userRights } = loginContext
  const { getTreatment } = useContext(ExperimentsContext)
  const { language } = useLanguageContext()

  const hasAllRequiredRights = (): boolean =>
    props.requiredRights.reduce((hasRequiredRights: boolean, requiredRight: Right) => {
      return hasRequiredRights && userRights.has(requiredRight)
    }, true)

  const hasRequiredExperiment = (): boolean => {
    if (!props.requiredExperiment) {
      return true
    }

    const treatment = getTreatment(props.requiredExperiment.experimentKey)
    return props.requiredExperiment.experimentEvaluator(treatment)
  }

  const hasRequiredLanguages = (): boolean => {
    if (!props.requiredLanguage) {
      return true
    }

    return props.requiredLanguage.includes(language)
  }

  if (hasAllRequiredRights() && hasRequiredExperiment() && hasRequiredLanguages()) {
    return <Route {...props} />
  }

  getMetricsPublisherChainAsync('AuthorizedRoute').then((p) => {
    p.publishCounter('unauthorizedRoute.all', 1)
      .publishCounter('unauthorizedRoute.requiredRights', !hasAllRequiredRights() ? 1 : 0)
      .publishCounter('unauthorizedRoute.requiredExperiment', !hasRequiredExperiment() ? 1 : 0)
      .publishCounter('unauthorizedRoute.requiredLanguages', !hasRequiredLanguages() ? 1 : 0)
      .publishString('unauthorizedRoute.path', `${props.path}`)
  })

  return <Redirect push to={{ pathname: '/error/unauthorized_access' }} />
}
